import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TurnstileComponent } from '../turnstile/turnstile.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { FilePickerComponent } from '../file-picker/file-picker.component';
import { DumbFormControlComponent } from './ui/dumb-form-control/dumb-form-control.component';
import { TableFormControlComponent } from './ui/table-form-control/table-form-control.component';
import { InvoiceFormControlComponent } from './ui/invoice-form-control/invoice-form-control.component';
import {
  DynamicFormConfiguration,
  DynamicFormControl,
} from './interfaces/dynamic-form-controls';
import { DialogComponent } from '../dialog/dialog.component';
import { heroXCircleSolid } from '@ng-icons/heroicons/solid';

export type FormState = 'idle' | 'loading' | 'success' | 'error';

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  providers: [provideIcons({ heroXCircleSolid })],
  imports: [
    CommonModule,
    TurnstileComponent,
    RouterModule,
    NgIconComponent,
    ReactiveFormsModule,
    FilePickerComponent,
    DumbFormControlComponent,
    TableFormControlComponent,
    InvoiceFormControlComponent,
    DialogComponent,
  ],
  template: `
    <form (submit)="submit($event)" [formGroup]="formGroup">
      <div
        *ngFor="let control of config.controls; let i = index"
        class="px-4 pt-2 pb-3"
        [ngClass]="{ 'section-control': control.section }"
      >
        <ng-container [ngSwitch]="control.type">
          <app-table-form-control
            *ngSwitchCase="'table'"
            [formGroup]="formGroup"
            [config]="control"
          ></app-table-form-control>
          <app-turnstile
            *ngSwitchCase="'turnstile'"
            [siteKey]="turnstileSiteKey"
            (resolved)="formGroup.get(control.name)?.setValue($event)"
          ></app-turnstile>
          <app-invoice-form-control
            *ngSwitchCase="'invoice'"
            [config]="control"
          ></app-invoice-form-control>
          <app-dumb-form-control
            *ngSwitchDefault
            [formGroup]="formGroup"
            [config]="control"
          ></app-dumb-form-control>
        </ng-container>
      </div>

      <div class="px-4 flex flex-row-reverse">
        <button
          type="submit"
          [disabled]="this.formGroup.invalid"
          class="submit-button"
        >
          Skicka
        </button>
      </div>

      <app-dialog *ngIf="state !== 'idle'" (closeDialog)="dialogClosed()">
        <ng-container my-dialog-body>
          <div class="flex items-center mb-2" *ngIf="state === 'loading'">
            <div role="status">
              <svg
                aria-hidden="true"
                class="inline w-6 h-6 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-green-500"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span class="sr-only">Loading...</span>
            </div>
            Skickar in...
          </div>

          <div *ngIf="state === 'success'">
            <div class="flex items-center mb-4">
              <svg
                class="w-6 h-6 me-2 text-primary-lighter flex-shrink-0"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"
                />
              </svg>
              Ärende inskickat
            </div>
            <div
              class="rounded-lg bg-slate-100 border border-gray-200 w-full mt-2 p-4 "
            >
              <label class="text-base font-normal block pb-4"
                >Ärendenummer:
                <input
                  class="font-mono block bg-white ml-[-2px]"
                  disabled
                  [value]="submitionId"
              /></label>
              <p>
                Du får en bekräftelse via e-post, vi återkommer så snart vi kan!
              </p>
            </div>
          </div>

          <div *ngIf="state === 'error'">
            <div class="flex items-center mb-4">
              <ng-icon
                name="heroXCircleSolid"
                class="block mr-2 text-red-700 text-lg"
              ></ng-icon>
              Fel uppstod
            </div>
            <div
              class="rounded-lg bg-red-100 border border-red-200 w-full mt-2 p-4 max-w-lg"
            >
              Det gick inte att skicka in ärendet. Vänligen försök igen senare
              eller kontakta CEOS.
            </div>
          </div>
        </ng-container>
      </app-dialog>
    </form>
  `,
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit {
  @Input() config!: DynamicFormConfiguration;
  @Input() turnstileSiteKey?: string;
  @Input() state: FormState = 'error';
  @Input() submitionId?: string;

  @Output() submitted = new EventEmitter<any>();

  formGroup: FormGroup = new FormGroup({});
  element: ElementRef = inject(ElementRef);

  ngOnInit() {
    this.config.controls.forEach((controlConfig) => {
      const dynamicFormControl = new DynamicFormControl(controlConfig);
      const { name, formControl } = dynamicFormControl.toNgFormControl();
      this.formGroup.addControl(name, formControl);
    });
  }

  getFormControlAsGroup(controlName: string): FormGroup {
    return this.formGroup.get(controlName) as FormGroup;
  }

  dialogClosed() {
    if (this.state === 'success') {
      this.formGroup.reset();
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
    }
    this.state = 'idle';
  }

  async submit(evt: SubmitEvent) {
    evt.preventDefault();
    evt.stopPropagation();

    if (this.formGroup.invalid) {
      const el = this.element.nativeElement.querySelector('form .ng-invalid');
      if (el) {
        el.focus();
        this.formGroup.get(el.id)?.markAsTouched();
        this.formGroup.get(el.id)?.markAsDirty();
      }
      return;
    }

    const formValue = this.formGroup.getRawValue();

    this.submitted.emit(formValue);
  }
}
